import React, { useState, useEffect } from "react";
import { db } from "../../config/firebase-config";
import { collection, query, where, getDocs, doc, getDoc, updateDoc, onSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import AnimatedNumber from "../../components/common/AnimatedNumber.jsx";

export default function EarningsDashboard({ user }) {
  const [earnings, setEarnings] = useState({
    requestEarnings: 0,
    referralEarnings: 0,
    totalEarned: 0,
    availableBalance: 0,
    pendingAmount: 0,
    totalRatings: 0,
    averageRating: 0
  });
  const [recentRequests, setRecentRequests] = useState([]);
  const [voucherHistory, setVoucherHistory] = useState([]);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [payoutFilter, setPayoutFilter] = useState("all"); // Changed from voucherFilter
  const [payoutSort, setPayoutSort] = useState("newest"); // Changed from voucherSort
  
  // PayPal states
  const [payoutMethod, setPayoutMethod] = useState("paypal");
  const [paypalEmail, setPaypalEmail] = useState("");
  const [showPayPalSetup, setShowPayPalSetup] = useState(false);
  const [paypalAmount, setPaypalAmount] = useState("");
  const [savingPayPalEmail, setSavingPayPalEmail] = useState(false);
  
  // Auto-payout states
  const [autoPayoutEnabled, setAutoPayoutEnabled] = useState(false);
  const [autoPayoutThreshold, setAutoPayoutThreshold] = useState(25);
  const [savingAutoPayoutSettings, setSavingAutoPayoutSettings] = useState(false);
  
  // PayPal status refresh state
  const [refreshingPayPalStatus, setRefreshingPayPalStatus] = useState(false);

  // Window size state for responsive design
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800
  });

  const functions = getFunctions();

  const voucherOptions = [
    { value: 20, label: "$20" },
    { value: 50, label: "$50" },
    { value: 100, label: "$100" },
    { value: 200, label: "$200" }
  ];

  // Handle window resize
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!user?.uid) return;
    
    fetchEarningsData();
    loadVoucherProducts();
    loadPayPalInfo();
    
    // Set up real-time listener for musician document to auto-refresh earnings
    const unsubscribeMusician = onSnapshot(
      doc(db, "musicians", user.uid),
      (doc) => {
        console.log("📄 Musician document updated, refreshing earnings...");
        fetchEarningsData();
      },
      (error) => {
        console.error("❌ Musician listener error:", error);
      }
    );
    
    // Set up real-time listener for pending requests to auto-refresh pending amount
    const unsubscribePendingRequests = onSnapshot(
      query(
        collection(db, "requests"),
        where("musicianId", "==", user.uid),
        where("status", "==", "pending_rating")
      ),
      (snapshot) => {
        console.log("📄 Pending requests updated, refreshing earnings...");
        console.log("🔍 Pending requests count:", snapshot.size);
        fetchEarningsData();
      },
      (error) => {
        console.error("❌ Pending requests listener error:", error);
      }
    );
    
    // Cleanup listeners when component unmounts
    return () => {
      unsubscribeMusician();
      unsubscribePendingRequests();
    };
  }, [user?.uid]);

  const loadVoucherProducts = async () => {
    try {
      setLoadingProducts(true);
      console.log('🔍 Loading voucher products...');
      
      // Direct fetch call since it's now an onRequest function
      const response = await fetch('https://us-central1-requestasong-4472b.cloudfunctions.net/getVoucherProducts', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      console.log('🔍 Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('🔍 Raw response:', result);
      
      if (result.success && result.products) {
        console.log('✅ Products loaded successfully:', result.products.length);
        setAvailableProducts(result.products);
        if (result.products.length > 0) {
          setSelectedProduct(result.products[0].id);
        }
      } else {
        throw new Error('Response missing expected data structure');
      }
      
    } catch (error) {
      console.error("❌ Failed to load voucher products:", error);
      
      // Enhanced fallback products for Australian market
      console.log('🔄 Using fallback products...');
      setAvailableProducts([
        {
          id: 'amazon-gift-card',
          name: 'Amazon Gift Card',
          description: 'Redeemable at Amazon Australia',
          countries: ['AU', 'GLOBAL']
        },
        {
          id: 'visa-gift-card', 
          name: 'Visa Gift Card',
          description: 'Use anywhere Visa is accepted',
          countries: ['AU', 'GLOBAL']
        },
        {
          id: 'google-play-gift-card',
          name: 'Google Play Gift Card', 
          description: 'For apps, games, and digital content',
          countries: ['AU', 'GLOBAL']
        }
      ]);
      setSelectedProduct('amazon-gift-card');
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchEarningsData = async () => {
    try {
      setLoading(true);

      // Get musician's profile to get earnings breakdown
      const musicianDoc = await getDoc(doc(db, "musicians", user.uid));
      let totalEarned = 0;
      let referralEarnings = 0;
      let requestEarnings = 0;
      let musicianTotalRedeemed = 0;
      
      if (musicianDoc.exists()) {
        const musicianData = musicianDoc.data();
        totalEarned = musicianData.totalEarnings || 0;
        referralEarnings = musicianData.referralEarnings || 0;
        requestEarnings = totalEarned - referralEarnings;
        musicianTotalRedeemed = musicianData.totalRedeemed || 0;
        
        console.log("🔍 Earnings breakdown:", {
          totalEarned,
          referralEarnings,
          requestEarnings,
          musicianTotalRedeemed
        });
      }

      // Fetch completed ratings/payments for rating stats and recent activity
      const ratingsQuery = query(
        collection(db, "ratings"),
        where("musicianId", "==", user.uid),
        where("paymentStatus", "in", ["captured", "auto_captured"])
      );
      const ratingsSnapshot = await getDocs(ratingsQuery);
      
      let totalRatings = 0;
      let ratingSum = 0;
      const requests = [];

      // Get all songIds to fetch song details
      const songIds = [];
      const ratingsData = [];

      ratingsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        totalRatings += 1;
        ratingSum += data.rating || 0;
        
        ratingsData.push({
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate()
        });

        if (data.songId) {
          songIds.push(data.songId);
        }
      });

      // Fetch song details from songs collection
      const songDetailsMap = {};
      if (songIds.length > 0) {
        const uniqueSongIds = [...new Set(songIds)];
        const songsQuery = query(
          collection(db, "songs"),
          where("__name__", "in", uniqueSongIds)
        );
        const songsSnapshot = await getDocs(songsQuery);
        
        songsSnapshot.docs.forEach(doc => {
          const songData = doc.data();
          songDetailsMap[doc.id] = {
            name: songData.name || "Unknown Song",
            artist: songData.artist || "Unknown Artist"
          };
        });
      }

      // Combine ratings with song details
      ratingsData.forEach(rating => {
        const songDetails = songDetailsMap[rating.songId] || {
          name: "Unknown Song",
          artist: "Unknown Artist"
        };
        
        requests.push({
          ...rating,
          songName: songDetails.name,
          artist: songDetails.artist
        });
      });

      // Fetch pending requests
      const pendingQuery = query(
        collection(db, "requests"),
        where("musicianId", "==", user.uid),
        where("status", "==", "pending_rating")
      );
      const pendingSnapshot = await getDocs(pendingQuery);
      
      let pendingAmount = 0;
      pendingSnapshot.docs.forEach(doc => {
        const requestData = doc.data();
        pendingAmount += requestData.baseAmount || requestData.amount || 0;
      });

      // Fetch voucher history
      const vouchersQuery = query(
        collection(db, "vouchers"),
        where("musicianId", "==", user.uid)
      );
      const vouchersSnapshot = await getDocs(vouchersQuery);
      
      const vouchers = [];
      let voucherTotalRedeemed = 0;
      vouchersSnapshot.docs.forEach(doc => {
        const data = doc.data();
        // Only count vouchers that were actually issued/delivered/used (same as backend logic)
        if (data.status === 'issued' || data.status === 'delivered' || data.status === 'used') {
          voucherTotalRedeemed += data.amount || 0;
        }
        vouchers.push({
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate()
        });
      });

      // Fetch PayPal payout history
      const payoutsQuery = query(
        collection(db, "payouts"),
        where("musicianId", "==", user.uid)
      );
      const payoutsSnapshot = await getDocs(payoutsQuery);
      
      const payouts = [];
      let paypalTotalRedeemed = 0;
      payoutsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.status === 'completed' || data.status === 'success') {
          paypalTotalRedeemed += data.amount || 0;
        }
        payouts.push({
          id: doc.id,
          ...data,
          timestamp: data.createdAt?.toDate() || new Date(),
          type: 'paypal' // Add type to distinguish from vouchers
        });
      });

      // Calculate total redeemed to match backend logic
      const totalRedeemed = voucherTotalRedeemed + paypalTotalRedeemed;
      // Apply 12% platform fee to match backend calculation
      const platformFeePercentage = 0.12;
      const platformFee = totalEarned * platformFeePercentage;
      const netEarnings = totalEarned - platformFee;
      const availableBalance = netEarnings - totalRedeemed;
      const averageRating = totalRatings > 0 ? (ratingSum / totalRatings) : 0;

      console.log("💰 Earnings calculation:", {
        totalEarned,
        platformFee,
        netEarnings,
        requestEarnings,
        referralEarnings,
        voucherTotalRedeemed,
        paypalTotalRedeemed, 
        totalRedeemed,
        availableBalance,
        musicianProfileRedeemed: musicianTotalRedeemed,
        note: "Now calculating vouchers from actual voucher collection to match backend"
      });

      setEarnings({
        requestEarnings,
        referralEarnings,
        totalEarned: netEarnings,
        grossEarnings: totalEarned, // Total before platform fee
        platformFee,
        availableBalance,
        pendingAmount,
        totalRatings,
        averageRating: Math.round(averageRating * 10) / 10
      });

      setRecentRequests(requests.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 10));
      setVoucherHistory(vouchers.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
      setPayoutHistory(payouts.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));

    } catch (error) {
      console.error("Error fetching earnings data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleVoucherSelection = (amount) => {
    setSelectedVoucher(amount.toString());
    setShowProductSelector(true);
  };

  const handleVoucherRedemption = async () => {
    if (!selectedVoucher || redeeming) return;

    const voucherAmount = parseInt(selectedVoucher);
    if (voucherAmount > earnings.availableBalance) {
      alert(`Insufficient balance. You have $${earnings.availableBalance.toFixed(2)} available.`);
      return;
    }

    setRedeeming(true);
    try {
      const redeemVoucher = httpsCallable(functions, 'redeemVoucher');
      
      console.log("Processing Tango gift card redemption...");
      const result = await redeemVoucher({
        amount: voucherAmount,
        productId: selectedProduct
      });
      
      if (result.data.success) {
        const { voucherCode, deliveryStatus, apiSuccess } = result.data;
        
        if (deliveryStatus === 'sent') {
          if (apiSuccess) {
            alert(`✅ Success!\n\n$${voucherAmount} gift card created and being delivered by Tango Card!\n\nCheck your email for the gift card.`);
          } else {
            alert(`⚠️ Gift card created but delivered as backup code.\n\nCode: ${voucherCode}\n\nContact support for assistance.`);
          }
        } else {
          alert(`⚠️ Gift Card Created!\n\nYour $${voucherAmount} gift card was created but email delivery failed.\n\nOrder ID: ${voucherCode}\n\nPlease contact support with this order ID.`);
        }
        
        setSelectedVoucher("");
        setSelectedProduct(availableProducts[0]?.id || "");
        setShowProductSelector(false);
        fetchEarningsData();
        
      } else {
        throw new Error(result.data.message || "Gift card creation failed");
      }

    } catch (error) {
      console.error("Error redeeming gift card:", error);
      
      if (error.code === 'functions/failed-precondition') {
        // Extract the actual error message from the backend which now includes balance info
        const errorMessage = error.message || "Insufficient balance for this gift card amount.";
        alert("❌ " + errorMessage);
      } else if (error.code === 'functions/invalid-argument') {
        alert("❌ Invalid gift card amount selected.");
      } else if (error.code === 'functions/unauthenticated') {
        alert("❌ Authentication issue. Please refresh and try again.");
      } else if (error.code === 'functions/unavailable') {
        alert("❌ " + (error.message || "Service temporarily unavailable. Please try again later."));
      } else {
        alert("❌ Error processing gift card redemption:\n\n" + error.message + "\n\nPlease try again or contact support.");
      }
    } finally {
      setRedeeming(false);
    }
  };

  const loadPayPalInfo = async () => {
    try {
      const idToken = await user.getIdToken();
      
      const response = await fetch('https://us-central1-requestasong-4472b.cloudfunctions.net/getPayPalInfoHttp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({})
      });
      
      const result = await response.json();
      
      if (result.success) {
        setPaypalEmail(result.paypalEmail || "");
        if (result.preferredPayoutMethod) {
          setPayoutMethod(result.preferredPayoutMethod);
        }
        if (result.autoPayoutSettings) {
          setAutoPayoutEnabled(result.autoPayoutSettings.enabled || false);
          setAutoPayoutThreshold(result.autoPayoutSettings.threshold || 25);
        }
      }
    } catch (error) {
      console.error("Error loading PayPal info:", error);
    }
  };
  
  const handleSaveAutoPayoutSettings = async () => {
    if (!paypalEmail) {
      alert("Please set up your PayPal email first before enabling auto-payouts");
      return;
    }

    try {
      setSavingAutoPayoutSettings(true);
      const idToken = await user.getIdToken();
      
      const response = await fetch('https://us-central1-requestasong-4472b.cloudfunctions.net/updatePayPalEmailHttp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ 
          paypalEmail,
          autoPayoutSettings: {
            enabled: autoPayoutEnabled,
            threshold: autoPayoutThreshold
          }
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        alert(`✅ Auto-payout settings saved!\n\n${autoPayoutEnabled ? 
          `Auto-payouts will be sent to ${paypalEmail} when your balance reaches $${autoPayoutThreshold}` :
          'Auto-payouts have been disabled'}`);
      } else {
        throw new Error(result.error || "Failed to save auto-payout settings");
      }
    } catch (error) {
      console.error("Error saving auto-payout settings:", error);
      alert("❌ Error saving auto-payout settings:\n\n" + error.message);
    } finally {
      setSavingAutoPayoutSettings(false);
    }
  };

  const handleSavePayPalEmail = async () => {
    if (!paypalEmail || !paypalEmail.includes('@')) {
      alert("Please enter a valid PayPal email address");
      return;
    }
    
    setSavingPayPalEmail(true);
    try {
      const idToken = await user.getIdToken();
      
      const response = await fetch('https://us-central1-requestasong-4472b.cloudfunctions.net/updatePayPalEmailHttp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ paypalEmail })
      });
      
      const result = await response.json();
      
      if (result.success) {
        alert("✅ PayPal email saved successfully!");
        setShowPayPalSetup(false);
        
        await updateDoc(doc(db, "musicians", user.uid), {
          preferredPayoutMethod: "paypal"
        });
      } else {
        throw new Error(result.error || "Failed to save PayPal email");
      }
    } catch (error) {
      console.error("Error saving PayPal email:", error);
      alert("❌ Error saving PayPal email: " + error.message);
    } finally {
      setSavingPayPalEmail(false);
    }
  };
  
  const handlePayPalPayout = async () => {
    const amount = parseFloat(paypalAmount);
    
    if (!amount || amount < 20) {
      alert("Minimum PayPal payout amount is $20 (to minimize PayPal fees for you)");
      return;
    }
    
    if (amount > earnings.availableBalance) {
      alert(`Insufficient balance. You have $${earnings.availableBalance.toFixed(2)} available.`);
      return;
    }
    
    if (!paypalEmail) {
      alert("Please set up your PayPal email first");
      setShowPayPalSetup(true);
      return;
    }
    
    setRedeeming(true);
    try {
      const idToken = await user.getIdToken();
      
      const response = await fetch('https://us-central1-requestasong-4472b.cloudfunctions.net/redeemViaPayPalHttp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ 
          amount, 
          paypalEmail 
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        alert(`✅ Success!\n\nPayPal payout of $${amount.toFixed(2)} has been sent to ${paypalEmail}.\n\nYou should receive it within a few minutes.`);
        setPaypalAmount("");
        setTimeout(() => {
          fetchEarningsData();
        }, 1000);
      } else {
        throw new Error(result.error || "PayPal payout failed");
      }
    } catch (error) {
      console.error("Error processing PayPal payout:", error);
      
      if (error.message.includes('PayPal is not properly configured')) {
        alert("❌ PayPal is not properly configured. Please contact support.");
      } else if (error.message.includes('Minimum payout amount')) {
        alert("❌ " + error.message);
      } else {
        alert("❌ Error processing PayPal payout:\n\n" + error.message + "\n\nPlease try again or contact support.");
      }
    } finally {
      setRedeeming(false);
    }
  };

  const refreshPayPalStatus = async () => {
    setRefreshingPayPalStatus(true);
    try {
      const response = await fetch('https://us-central1-requestasong-4472b.cloudfunctions.net/triggerPayPalStatusPoll', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        alert(`✅ PayPal status refresh completed!\n\n${result.message}\n\nPending found: ${result.pendingFound || 0}\nUpdated: ${result.updated || 0}`);
        
        // Refresh the payout history to show updated statuses
        setTimeout(() => {
          fetchEarningsData();
        }, 1000);
      } else {
        throw new Error(result.error || "Failed to refresh PayPal status");
      }
    } catch (error) {
      console.error("Error refreshing PayPal status:", error);
      alert("❌ Error refreshing PayPal status:\n\n" + error.message + "\n\nPlease try again.");
    } finally {
      setRefreshingPayPalStatus(false);
    }
  };

  const getAvailableVouchers = () => {
    return voucherOptions.filter(voucher => voucher.value <= earnings.availableBalance);
  };

  const getFilteredAndSortedPayouts = () => {
    // Combine vouchers and payouts into one array
    const combinedPayouts = [
      ...voucherHistory.map(voucher => ({ ...voucher, type: 'voucher' })),
      ...payoutHistory
    ];

    return combinedPayouts
      .filter(payout => {
        if (payoutFilter === 'all') return true;
        if (payoutFilter === 'voucher') return payout.type === 'voucher';
        if (payoutFilter === 'paypal') return payout.type === 'paypal';
        // For voucher-specific status filters
        return payout.status === payoutFilter;
      })
      .sort((a, b) => {
        if (payoutSort === 'newest') {
          return new Date(b.timestamp) - new Date(a.timestamp);
        } else if (payoutSort === 'oldest') {
          return new Date(a.timestamp) - new Date(b.timestamp);
        } else if (payoutSort === 'amount_high') {
          return b.amount - a.amount;
        } else if (payoutSort === 'amount_low') {
          return a.amount - b.amount;
        }
        return 0;
      });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'issued': return { bg: '#065F46', text: '#A7F3D0', border: '#10B981' };
      case 'used': return { bg: '#374151', text: '#9CA3AF', border: '#6B7280' };
      case 'expired': return { bg: '#92400E', text: '#FCD34D', border: '#F59E0B' };
      default: return { bg: '#374151', text: '#9CA3AF', border: '#6B7280' };
    }
  };

  const formatCurrency = (amount) => `$${amount.toFixed(2)}`;
  const formatCurrencyAnimated = (value) => `$${value.toFixed(2)}`;

  const calculatePayPalFees = (amount) => {
    if (!amount || amount <= 0) return { fee: 0, netAmount: 0 };
    const fixedFee = 0.30; // AUD $0.30 fixed fee (PayPal business account)
    const percentageFee = amount * 0.026; // 2.6% for business accounts (Goods & Services)
    const totalFee = fixedFee + percentageFee;
    const netAmount = amount - totalFee;
    return {
      fee: totalFee,
      netAmount: Math.max(0, netAmount),
      feePercentage: amount > 0 ? (totalFee / amount * 100) : 0
    };
  };

  const getRatingStars = (rating) => {
    return "⭐".repeat(Math.round(rating));
  };

  // Use window size state instead of direct window access
  const isMobile = windowSize.width < 640;
  const isTablet = windowSize.width < 768;

  const cardStyle = {
    backgroundColor: '#1F2937',
    border: '4px solid #7C3AED',
    borderRadius: '16px',
    padding: isMobile ? '20px' : '24px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
    marginBottom: '24px'
  };

  const statCardStyle = {
    backgroundColor: '#374151',
    border: '3px solid #4B5563',
    borderRadius: '12px',
    padding: isMobile ? '16px' : '20px',
    textAlign: 'center',
    transition: 'all 0.3s ease'
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: 'white' }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '4px solid #374151',
          borderTop: '4px solid #7C3AED',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 20px'
        }}></div>
        <p style={{ color: '#C4B5FD', fontSize: '18px' }}>Loading earnings data...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <div style={{ color: 'white' }}>
      {/* Available Balance & Pending Cards - MOVED TO TOP */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
        gap: '16px',
        marginBottom: '32px'
      }}>
        {/* Available Balance */}
        <div className="available-balance" style={{
          ...statCardStyle,
          borderColor: '#3B82F6'
        }}>
          <div style={{ fontSize: isMobile ? '28px' : '32px', marginBottom: '8px' }}>💰</div>
          <p style={{ fontSize: isMobile ? '12px' : '14px', color: '#3B82F6', fontWeight: 'bold', margin: '0 0 4px 0' }}>AVAILABLE BALANCE</p>
          <p style={{ fontSize: isMobile ? '18px' : '24px', fontWeight: 'bold', color: 'white', margin: '0 0 4px 0' }}>
            <AnimatedNumber 
              value={earnings.availableBalance} 
              formatFunction={formatCurrencyAnimated}
              duration={2000}
              delay={100}
            />
          </p>
          <p style={{ fontSize: '12px', color: '#9CA3AF', margin: 0 }}>Ready for gift cards</p>
        </div>

        {/* Pending */}
        <div style={{
          ...statCardStyle,
          borderColor: '#F59E0B'
        }}>
          <div style={{ fontSize: isMobile ? '28px' : '32px', marginBottom: '8px' }}>⏳</div>
          <p style={{ fontSize: isMobile ? '12px' : '14px', color: '#F59E0B', fontWeight: 'bold', margin: '0 0 4px 0' }}>PENDING AMOUNT</p>
          <p style={{ fontSize: isMobile ? '18px' : '24px', fontWeight: 'bold', color: 'white', margin: '0 0 4px 0' }}>
            <AnimatedNumber 
              value={earnings.pendingAmount} 
              formatFunction={formatCurrencyAnimated}
              duration={2000}
              delay={200}
            />
          </p>
          <p style={{ fontSize: '12px', color: '#9CA3AF', margin: 0 }}>Awaiting rating</p>
        </div>
      </div>

      {/* Stats Cards - Compact Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)',
        gap: '16px',
        marginBottom: '32px'
      }}>
        {/* Request Earnings */}
        <div className="request-earnings" style={{
          ...statCardStyle,
          borderColor: '#10B981'
        }}>
          <div style={{ fontSize: isMobile ? '28px' : '32px', marginBottom: '8px' }}>🎵</div>
          <p style={{ fontSize: isMobile ? '12px' : '14px', color: '#10B981', fontWeight: 'bold', margin: '0 0 4px 0' }}>REQUEST EARNINGS</p>
          <p style={{ fontSize: isMobile ? '18px' : '24px', fontWeight: 'bold', color: 'white', margin: '0 0 4px 0' }}>
            <AnimatedNumber 
              value={earnings.requestEarnings} 
              formatFunction={formatCurrencyAnimated}
              duration={2000}
              delay={300}
            />
          </p>
          <p style={{ fontSize: '12px', color: '#9CA3AF', margin: 0 }}>
            {earnings.totalRatings} song requests
          </p>
        </div>

        {/* Referral Earnings */}
        <div className="referral-earnings" style={{
          ...statCardStyle,
          borderColor: '#8B5CF6'
        }}>
          <div style={{ fontSize: isMobile ? '28px' : '32px', marginBottom: '8px' }}>🔗</div>
          <p style={{ fontSize: isMobile ? '12px' : '14px', color: '#8B5CF6', fontWeight: 'bold', margin: '0 0 4px 0' }}>REFERRAL EARNINGS</p>
          <p style={{ fontSize: isMobile ? '18px' : '24px', fontWeight: 'bold', color: 'white', margin: '0 0 4px 0' }}>
            <AnimatedNumber 
              value={earnings.referralEarnings} 
              formatFunction={formatCurrencyAnimated}
              duration={2000}
              delay={400}
            />
          </p>
          <p style={{ fontSize: '12px', color: '#9CA3AF', margin: 0 }}>
            Commission from referrals
          </p>
        </div>

        {/* Total Earned */}
        <div className="total-earnings" style={{
          ...statCardStyle,
          borderColor: '#F59E0B'
        }}>
          <div style={{ fontSize: isMobile ? '28px' : '32px', marginBottom: '8px' }}>💸</div>
          <p style={{ fontSize: isMobile ? '12px' : '14px', color: '#F59E0B', fontWeight: 'bold', margin: '0 0 4px 0' }}>TOTAL EARNINGS</p>
          <p style={{ fontSize: isMobile ? '18px' : '24px', fontWeight: 'bold', color: 'white', margin: '0 0 4px 0' }}>
            <AnimatedNumber 
              value={earnings.totalEarned} 
              formatFunction={formatCurrencyAnimated}
              duration={2000}
              delay={500}
            />
          </p>
          <p style={{ fontSize: '12px', color: '#9CA3AF', margin: 0 }}>
            Combined total
          </p>
        </div>

      </div>

      {/* Performance Overview */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 'bold', color: 'white', marginBottom: '20px', display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: '12px' }}>🎯</span>
          Performance Overview
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: '24px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>{getRatingStars(earnings.averageRating)}</div>
            <p style={{ fontSize: '24px', fontWeight: 'bold', color: 'white', margin: '0 0 4px 0' }}>{earnings.averageRating}/5</p>
            <p style={{ color: '#C4B5FD', fontSize: '14px', margin: 0 }}>Average Rating</p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '32px', fontWeight: 'bold', color: 'white', margin: '0 0 8px 0' }}>{earnings.totalRatings}</p>
            <p style={{ color: '#C4B5FD', fontSize: '14px', margin: 0 }}>Total Requests</p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '24px', fontWeight: 'bold', color: 'white', margin: '0 0 8px 0' }}>
              {earnings.totalRatings > 0 ? formatCurrency(earnings.totalEarned / earnings.totalRatings) : "$0.00"}
            </p>
            <p style={{ color: '#C4B5FD', fontSize: '14px', margin: 0 }}>Average per Request</p>
          </div>
        </div>
      </div>

      {/* Enhanced Payout Section */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 'bold', color: 'white', marginBottom: '20px', display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: '12px' }}>💰</span>
          Redeem Earnings
        </h2>
        
        {/* Payout Method Selector */}
        {earnings.availableBalance >= 20 && (
          <div style={{ marginBottom: '24px' }}>
            <div style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '20px'
            }}>
              {earnings.availableBalance >= 20 && (
                <button
                  onClick={() => setPayoutMethod('voucher')}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '12px',
                    border: payoutMethod === 'voucher' ? '3px solid #7C3AED' : '3px solid #4B5563',
                    backgroundColor: payoutMethod === 'voucher' ? '#581C87' : '#374151',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  🎁 Gift Cards
                </button>
              )}
              <button
                onClick={() => setPayoutMethod('paypal')}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '12px',
                  border: payoutMethod === 'paypal' ? '3px solid #0070BA' : '3px solid #4B5563',
                  backgroundColor: payoutMethod === 'paypal' ? '#003087' : '#374151',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                💳 PayPal
              </button>
            </div>
          </div>
        )}
        
        {earnings.availableBalance >= 20 ? (
          <div>
            {/* Balance Display */}
            <div style={{
              backgroundColor: '#1E3A8A',
              border: '3px solid #3B82F6',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              <p style={{ fontSize: '18px', fontWeight: 'bold', color: 'white', margin: 0 }}>
                Available Balance: <span style={{ color: '#60A5FA' }}>{formatCurrency(earnings.availableBalance)}</span>
              </p>
            </div>
            
            {/* Conditional UI based on payout method */}
            {payoutMethod === 'voucher' ? (
              <>
                {/* Voucher Selection UI */}
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ color: 'white', fontSize: '18px', marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '8px' }}>1️⃣</span>
                    Choose Gift Card Amount
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
                    gap: '12px'
                  }}>
                    {getAvailableVouchers().map(voucher => (
                      <button
                        key={voucher.value}
                        onClick={() => handleVoucherSelection(voucher.value)}
                        style={{
                          padding: '16px',
                          borderRadius: '12px',
                          border: selectedVoucher === voucher.value.toString() ? '3px solid #7C3AED' : '3px solid #4B5563',
                          backgroundColor: selectedVoucher === voucher.value.toString() ? '#581C87' : '#374151',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '16px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          textAlign: 'center'
                        }}
                        onMouseOver={(e) => {
                          if (selectedVoucher !== voucher.value.toString()) {
                            e.target.style.backgroundColor = '#4B5563';
                            e.target.style.transform = 'scale(1.02)';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (selectedVoucher !== voucher.value.toString()) {
                            e.target.style.backgroundColor = '#374151';
                            e.target.style.transform = 'scale(1)';
                          }
                        }}
                      >
                        {voucher.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Step 2: Product Selection */}
                {showProductSelector && (
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ color: 'white', fontSize: '18px', marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
                      <span style={{ marginRight: '8px' }}>2️⃣</span>
                      Choose Gift Card Type
                    </h3>
                    
                    {loadingProducts ? (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#9CA3AF' }}>
                        <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
                        Loading gift card options...
                      </div>
                    ) : (
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: '1fr',
                        gap: '12px' 
                      }}>
                        {availableProducts.map(product => (
                          <div
                            key={product.id}
                            onClick={() => setSelectedProduct(product.id)}
                            style={{
                              padding: '16px',
                              borderRadius: '12px',
                              border: selectedProduct === product.id ? '3px solid #10B981' : '3px solid #4B5563',
                              backgroundColor: selectedProduct === product.id ? '#065F46' : '#374151',
                              color: 'white',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseOver={(e) => {
                              if (selectedProduct !== product.id) {
                                e.target.style.backgroundColor = '#4B5563';
                                e.target.style.transform = 'scale(1.01)';
                              }
                            }}
                            onMouseOut={(e) => {
                              if (selectedProduct !== product.id) {
                                e.target.style.backgroundColor = '#374151';
                                e.target.style.transform = 'scale(1)';
                              }
                            }}
                          >
                            <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '4px', display: 'flex', alignItems: 'center' }}>
                              <span style={{ marginRight: '8px' }}>🎁</span>
                              {product.name}
                            </div>
                            {product.description && (
                              <div style={{ fontSize: '14px', color: '#9CA3AF' }}>
                                {product.description}
                              </div>
                            )}
                            {product.countries && (
                              <div style={{ fontSize: '12px', color: '#60A5FA', marginTop: '4px' }}>
                                Available in: {product.countries.join(', ')}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Step 3: Confirm Redemption */}
                {selectedVoucher && selectedProduct && (
                  <div style={{
                    backgroundColor: '#064E3B',
                    border: '3px solid #10B981',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '20px'
                  }}>
                    <h4 style={{ color: 'white', margin: '0 0 12px 0', display: 'flex', alignItems: 'center' }}>
                      <span style={{ marginRight: '8px' }}>3️⃣</span>
                      Confirm Your Gift Card:
                    </h4>
                    <p style={{ color: '#A7F3D0', margin: '0 0 8px 0' }}>
                      <strong>Amount:</strong> ${selectedVoucher}
                    </p>
                    <p style={{ color: '#A7F3D0', margin: '0 0 8px 0' }}>
                      <strong>Type:</strong> {availableProducts.find(p => p.id === selectedProduct)?.name}
                    </p>
                    <p style={{ color: '#A7F3D0', margin: '0 0 16px 0', fontSize: '14px' }}>
                      ✨ This will be a gift card delivered by Tango Card to your email!
                    </p>
                    
                    <button
                      onClick={handleVoucherRedemption}
                      disabled={redeeming}
                      style={{
                        backgroundColor: redeeming ? '#6B7280' : '#059669',
                        borderColor: redeeming ? '#6B7280' : '#10B981',
                        border: '3px solid',
                        borderRadius: '12px',
                        color: 'white',
                        fontWeight: 'bold',
                        cursor: redeeming ? 'not-allowed' : 'pointer',
                        opacity: redeeming ? 0.6 : 1,
                        width: '100%',
                        fontSize: '18px',
                        padding: '16px',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onMouseOver={(e) => {
                        if (!redeeming) {
                          e.target.style.backgroundColor = '#047857';
                          e.target.style.transform = 'scale(1.02)';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (!redeeming) {
                          e.target.style.backgroundColor = '#059669';
                          e.target.style.transform = 'scale(1)';
                        }
                      }}
                    >
                      {redeeming ? (
                        <>
                          <span style={{ marginRight: '8px' }}>⏳</span>
                          Processing...
                        </>
                      ) : (
                        <>
                          <span style={{ marginRight: '8px' }}>🎁</span>
                          Redeem ${selectedVoucher} Gift Card
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Info about gift cards */}
                <div style={{
                  backgroundColor: '#1E3A8A',
                  border: '3px solid #3B82F6',
                  borderRadius: '12px',
                  padding: '16px',
                  marginTop: '16px'
                }}>
                  <h4 style={{ color: '#BFDBFE', fontSize: '16px', margin: '0 0 8px 0', display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '8px' }}>ℹ️</span>
                    About Your Gift Cards
                  </h4>
                  <div style={{ color: '#BFDBFE', fontSize: '14px', lineHeight: '1.5' }}>
                    <p style={{ margin: '0 0 8px 0' }}>• Gift cards delivered to your email</p>
                    <p style={{ margin: '0 0 8px 0' }}>• Delivered directly to your email within minutes</p>
                    <p style={{ margin: '0 0 8px 0' }}>• Use them just like any store-bought gift card</p>
                    <p style={{ margin: 0 }}>• No expiry dates on most gift cards</p>
                  </div>
                </div>
              </>
            ) : (
              <div>
                {/* PayPal Payout UI */}
                <div style={{ marginBottom: '24px' }}>
                  {/* PayPal Email Setup */}
                  {(!paypalEmail || showPayPalSetup) ? (
                    <div style={{
                      backgroundColor: '#003087',
                      border: '3px solid #0070BA',
                      borderRadius: '12px',
                      padding: '20px',
                      marginBottom: '20px'
                    }}>
                      <h3 style={{ color: 'white', fontSize: '18px', marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: '8px' }}>📧</span>
                        Set Up PayPal Email
                      </h3>
                      <input
                        type="email"
                        value={paypalEmail}
                        onChange={(e) => setPaypalEmail(e.target.value)}
                        placeholder="your-email@example.com"
                        style={{
                          width: '100%',
                          padding: '12px',
                          borderRadius: '8px',
                          border: '2px solid #0070BA',
                          backgroundColor: '#1F2937',
                          color: 'white',
                          fontSize: '16px',
                          marginBottom: '12px'
                        }}
                      />
                      <button
                        onClick={handleSavePayPalEmail}
                        disabled={savingPayPalEmail || !paypalEmail.includes('@')}
                        style={{
                          width: '100%',
                          padding: '12px',
                          borderRadius: '8px',
                          border: '3px solid #0070BA',
                          backgroundColor: savingPayPalEmail ? '#6B7280' : '#0070BA',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '16px',
                          cursor: savingPayPalEmail ? 'not-allowed' : 'pointer',
                          opacity: savingPayPalEmail || !paypalEmail.includes('@') ? 0.6 : 1
                        }}
                      >
                        {savingPayPalEmail ? 'Saving...' : 'Save PayPal Email'}
                      </button>
                    </div>
                  ) : (
                    <div>
                      {/* Auto-Payout Settings */}
                      <div style={{
                        backgroundColor: '#003087',
                        border: '2px solid #0070BA',
                        borderRadius: '12px',
                        padding: '16px',
                        marginBottom: '24px'
                      }}>
                        <h3 style={{
                          color: '#BFDBFE',
                          fontSize: '16px',
                          marginBottom: '16px',
                          margin: '0 0 16px 0',
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          <span style={{ marginRight: '8px' }}>🤖</span>
                          Auto-Payout Settings
                        </h3>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          {/* Enable/Disable Toggle */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '8px' }}>
                              <input
                                type="checkbox"
                                checked={autoPayoutEnabled}
                                onChange={(e) => setAutoPayoutEnabled(e.target.checked)}
                                style={{
                                  width: '18px',
                                  height: '18px',
                                  accentColor: '#0070BA'
                                }}
                              />
                              <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>
                                Enable automatic PayPal payouts
                              </span>
                            </label>
                          </div>
                          
                          {/* Threshold Selection */}
                          {autoPayoutEnabled && (
                            <div>
                              <label style={{ color: '#BFDBFE', fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                                Auto-payout when balance reaches:
                              </label>
                              <select
                                value={autoPayoutThreshold}
                                onChange={(e) => setAutoPayoutThreshold(parseInt(e.target.value))}
                                style={{
                                  width: '100%',
                                  padding: '10px',
                                  borderRadius: '8px',
                                  border: '2px solid #0070BA',
                                  backgroundColor: '#003087',
                                  color: 'white',
                                  fontSize: '14px'
                                }}
                              >
                                <option value={25}>$25 (fees: ~3.8%)</option>
                                <option value={50}>$50 (fees: ~3.2%)</option>
                                <option value={100}>$100 (fees: ~2.9%)</option>
                                <option value={200}>$200 (fees: ~2.75%)</option>
                              </select>
                            </div>
                          )}
                          
                          {/* Info Text */}
                          <div style={{
                            backgroundColor: '#1E3A8A',
                            border: '1px solid #3B82F6',
                            borderRadius: '8px',
                            padding: '12px'
                          }}>
                            <p style={{
                              color: '#BFDBFE',
                              fontSize: '13px',
                              margin: 0,
                              lineHeight: '1.4'
                            }}>
                              {autoPayoutEnabled ? (
                                <>
                                  ✅ When your balance reaches ${autoPayoutThreshold}, we'll automatically send a payout to <strong>{paypalEmail}</strong>. 
                                  You'll receive an email notification when this happens. You can still make manual payouts anytime.
                                </>
                              ) : (
                                <>
                                  ⚠️ Auto-payouts are disabled. You can manually request payouts when your balance reaches $20 or more.
                                </>
                              )}
                            </p>
                          </div>
                          
                          {/* Save Button */}
                          <button
                            onClick={handleSaveAutoPayoutSettings}
                            disabled={savingAutoPayoutSettings}
                            style={{
                              width: '100%',
                              padding: '12px',
                              borderRadius: '8px',
                              border: '2px solid #0070BA',
                              backgroundColor: savingAutoPayoutSettings ? '#6B7280' : '#0070BA',
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '14px',
                              cursor: savingAutoPayoutSettings ? 'not-allowed' : 'pointer',
                              opacity: savingAutoPayoutSettings ? 0.6 : 1
                            }}
                          >
                            {savingAutoPayoutSettings ? 'Saving...' : 'Save Auto-Payout Settings'}
                          </button>
                        </div>
                      </div>

                      <div>
                        <h3 style={{ color: 'white', fontSize: '18px', marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
                          <span style={{ marginRight: '8px' }}>💵</span>
                          Enter Payout Amount
                        </h3>
                      
                        {/* Current PayPal Email Display */}
                        <div style={{
                          backgroundColor: '#1E3A8A',
                          border: '2px solid #3B82F6',
                          borderRadius: '8px',
                          padding: '12px',
                          marginBottom: '16px'
                        }}>
                          <p style={{ color: '#BFDBFE', fontSize: '14px', margin: '0 0 4px 0' }}>PayPal Email:</p>
                          <p style={{ color: 'white', fontSize: '16px', margin: 0, fontWeight: 'bold' }}>
                            {paypalEmail}
                            <button
                              onClick={() => setShowPayPalSetup(true)}
                              style={{
                                marginLeft: '12px',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                border: '1px solid #3B82F6',
                                backgroundColor: 'transparent',
                                color: '#3B82F6',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                            >
                              Change
                            </button>
                          </p>
                        </div>
                        
                        {/* Amount Input */}
                        <div style={{
                          display: 'flex',
                          gap: '12px',
                          marginBottom: '20px'
                        }}>
                          <input
                            type="number"
                            value={paypalAmount}
                            onChange={(e) => setPaypalAmount(e.target.value)}
                            placeholder="Enter amount (min $20)"
                            min="20"
                            max={earnings.availableBalance}
                            step="0.01"
                            style={{
                              flex: 1,
                              padding: '12px',
                              borderRadius: '8px',
                              border: '2px solid #0070BA',
                              backgroundColor: '#1F2937',
                              color: 'white',
                              fontSize: '16px'
                            }}
                          />
                          <button
                            onClick={handlePayPalPayout}
                            disabled={redeeming || !paypalAmount || parseFloat(paypalAmount) < 20}
                            style={{
                              padding: '12px 24px',
                              borderRadius: '8px',
                              border: '3px solid #0070BA',
                              backgroundColor: redeeming ? '#6B7280' : '#0070BA',
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '16px',
                              cursor: redeeming ? 'not-allowed' : 'pointer',
                              opacity: redeeming || !paypalAmount || parseFloat(paypalAmount) < 20 ? 0.6 : 1,
                              transition: 'all 0.3s ease'
                            }}
                          >
                            {redeeming ? 'Processing...' : 'Send to PayPal'}
                          </button>
                        </div>

                        {/* PayPal Fee Calculator */}
                        {paypalAmount && parseFloat(paypalAmount) > 0 && (
                          <div style={{
                            backgroundColor: '#0F172A',
                            border: '2px solid #0070BA',
                            borderRadius: '12px',
                            padding: '16px',
                            marginTop: '16px'
                          }}>
                            <h4 style={{
                              color: '#BFDBFE',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              marginBottom: '12px',
                              margin: '0 0 12px 0',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              <span style={{ marginRight: '8px' }}>💰</span>
                              PayPal Fee Breakdown
                            </h4>
                            {(() => {
                              const amount = parseFloat(paypalAmount);
                              const feeInfo = calculatePayPalFees(amount);
                              return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ color: '#9CA3AF', fontSize: '14px' }}>Payout Amount:</span>
                                    <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>
                                      ${amount.toFixed(2)}
                                    </span>
                                  </div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ color: '#9CA3AF', fontSize: '14px' }}>PayPal Fees:</span>
                                    <span style={{ color: '#EF4444', fontSize: '14px', fontWeight: 'bold' }}>
                                      -${feeInfo.fee.toFixed(2)} ({feeInfo.feePercentage.toFixed(1)}%)
                                    </span>
                                  </div>
                                  <div style={{
                                    borderTop: '1px solid #374151',
                                    paddingTop: '8px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                  }}>
                                    <span style={{ color: '#10B981', fontSize: '15px', fontWeight: 'bold' }}>You'll Receive:</span>
                                    <span style={{ color: '#10B981', fontSize: '16px', fontWeight: 'bold' }}>
                                      ${feeInfo.netAmount.toFixed(2)}
                                    </span>
                                  </div>
                                  {feeInfo.feePercentage > 5 && (
                                    <div style={{
                                      backgroundColor: '#7C2D12',
                                      border: '1px solid #EA580C',
                                      borderRadius: '8px',
                                      padding: '8px',
                                      marginTop: '8px'
                                    }}>
                                      <p style={{
                                        color: '#FED7AA',
                                        fontSize: '12px',
                                        margin: 0,
                                        display: 'flex',
                                        alignItems: 'center'
                                      }}>
                                        <span style={{ marginRight: '6px' }}>⚠️</span>
                                        High fee percentage! Consider waiting for a larger balance to minimize fees.
                                      </p>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        )}
                        
                        {/* Quick amount buttons */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(4, 1fr)',
                          gap: '8px',
                          marginBottom: '20px'
                        }}>
                          {[20, 50, 100, 200].filter(amt => amt <= earnings.availableBalance).map(amount => (
                            <button
                              key={amount}
                              onClick={() => setPaypalAmount(amount.toString())}
                              style={{
                                padding: '8px',
                                borderRadius: '8px',
                                border: paypalAmount === amount.toString() ? '2px solid #0070BA' : '2px solid #4B5563',
                                backgroundColor: paypalAmount === amount.toString() ? '#003087' : '#374151',
                                color: 'white',
                                fontSize: '14px',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease'
                              }}
                            >
                              ${amount}
                            </button>
                          ))}
                        </div>
                        
                        {/* Info about PayPal */}
                        <div style={{
                          backgroundColor: '#003087',
                          border: '3px solid #0070BA',
                          borderRadius: '12px',
                          padding: '16px',
                          marginTop: '16px'
                        }}>
                          <h4 style={{ color: '#B8D4F1', fontSize: '16px', margin: '0 0 8px 0', display: 'flex', alignItems: 'center' }}>
                            <span style={{ marginRight: '8px' }}>ℹ️</span>
                            About PayPal Payouts
                          </h4>
                          <div style={{ color: '#B8D4F1', fontSize: '14px', lineHeight: '1.5' }}>
                            <p style={{ margin: '0 0 8px 0' }}>• Direct deposit to your PayPal account</p>
                            <p style={{ margin: '0 0 8px 0' }}>• Usually arrives within minutes</p>
                            <p style={{ margin: '0 0 8px 0' }}>• Transfer to your bank or use online</p>
                            <p style={{ margin: '0 0 8px 0' }}>• No additional fees from RequestASong</p>
                            <p style={{ margin: 0 }}>• Minimum PayPal payout: $20 (lower fees for you)</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '32px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎪</div>
            <p style={{ fontSize: '20px', fontWeight: 'bold', color: 'white', marginBottom: '8px' }}>Keep performing to earn gift cards!</p>
            <p style={{ color: '#C4B5FD', fontSize: '16px', marginBottom: '4px' }}>Minimum balance of $20 required for both PayPal and gift cards</p>
            <p style={{ color: '#C4B5FD', fontSize: '16px' }}>You need {formatCurrency(20 - earnings.availableBalance)} more</p>
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: '24px'
      }}>
        {/* Recent Requests */}
        <div style={cardStyle}>
          <h2 style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: 'bold', color: 'white', marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '12px' }}>🎵</span>
            Recent Requests
          </h2>
          
          {recentRequests.length > 0 ? (
            <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
              {recentRequests.map((request, index) => (
                <div key={request.id} style={{
                  borderBottom: index !== recentRequests.length - 1 ? '2px solid #374151' : 'none',
                  paddingBottom: index !== recentRequests.length - 1 ? '16px' : '0',
                  marginBottom: index !== recentRequests.length - 1 ? '16px' : '0'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontWeight: 'bold', color: 'white', fontSize: '16px', margin: '0 0 4px 0' }}>
                        {request.songName || "Unknown Song"}
                      </p>
                      <p style={{ color: '#C4B5FD', fontSize: '14px', margin: '0 0 4px 0' }}>
                        {request.artist || "Unknown Artist"}
                      </p>
                      <p style={{ color: '#9CA3AF', fontSize: '12px', margin: 0 }}>
                        {request.timestamp?.toLocaleDateString()}
                      </p>
                    </div>
                    <div style={{ textAlign: 'right', marginLeft: '16px' }}>
                      <p style={{ fontWeight: 'bold', color: '#10B981', fontSize: '16px', margin: '0 0 4px 0' }}>
                        {formatCurrency(request.amount || 0)}
                      </p>
                      <p style={{ fontSize: '14px', color: 'white', margin: 0 }}>
                        {getRatingStars(request.rating)} ({request.rating}/5)
                      </p>
                    </div>
                  </div>
                  {request.comment && (
                    <p style={{ color: '#9CA3AF', fontSize: '14px', fontStyle: 'italic', marginTop: '8px', margin: '8px 0 0 0' }}>
                      "{request.comment}"
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '32px', color: '#9CA3AF' }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>🎵</div>
              <p>No completed requests yet</p>
            </div>
          )}
        </div>

        {/* Enhanced Payout History */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
            <h2 style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: 'bold', color: 'white', margin: 0, display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: '12px' }}>💳</span>
              Payout History ({voucherHistory.length + payoutHistory.length})
            </h2>
            {payoutHistory.some(p => p.status === 'PENDING') && (
              <button
                onClick={refreshPayPalStatus}
                disabled={refreshingPayPalStatus}
                style={{
                  padding: '6px 12px',
                  backgroundColor: refreshingPayPalStatus ? '#6B7280' : '#F59E0B',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: refreshingPayPalStatus ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s',
                  opacity: refreshingPayPalStatus ? 0.7 : 1
                }}
                onMouseOver={(e) => {
                  if (!refreshingPayPalStatus) {
                    e.target.style.backgroundColor = '#D97706';
                  }
                }}
                onMouseOut={(e) => {
                  if (!refreshingPayPalStatus) {
                    e.target.style.backgroundColor = '#F59E0B';
                  }
                }}
              >
                {refreshingPayPalStatus ? (
                  <>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      border: '2px solid #9CA3AF',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Checking...
                  </>
                ) : (
                  <>
                    <span>🔄</span>
                    Check PayPal Status
                  </>
                )}
              </button>
            )}
          </div>

          {/* Filters and Sorting */}
          <div style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            gap: '8px', 
            marginBottom: '16px' 
          }}>
            <select
              value={payoutFilter}
              onChange={(e) => setPayoutFilter(e.target.value)}
              style={{
                backgroundColor: '#374151',
                border: '2px solid #4B5563',
                borderRadius: '8px',
                color: 'white',
                padding: '6px 10px',
                fontSize: '13px',
                flex: 1
              }}
            >
              <option value="all">All Payouts</option>
              <option value="voucher">Gift Cards</option>
              <option value="paypal">PayPal</option>
            </select>

            <select
              value={payoutSort}
              onChange={(e) => setPayoutSort(e.target.value)}
              style={{
                backgroundColor: '#374151',
                border: '2px solid #4B5563',
                borderRadius: '8px',
                color: 'white',
                padding: '6px 10px',
                fontSize: '13px',
                flex: 1
              }}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="amount_high">Highest Amount</option>
              <option value="amount_low">Lowest Amount</option>
            </select>
          </div>
          
          {getFilteredAndSortedPayouts().length > 0 ? (
            <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
              {getFilteredAndSortedPayouts().map((payout, index) => {
                const statusColors = getStatusColor(payout.status);
                const filteredPayouts = getFilteredAndSortedPayouts();
                
                return (
                  <div key={payout.id} style={{
                    borderBottom: index !== filteredPayouts.length - 1 ? '2px solid #374151' : 'none',
                    paddingBottom: index !== filteredPayouts.length - 1 ? '16px' : '0',
                    marginBottom: index !== filteredPayouts.length - 1 ? '16px' : '0'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontWeight: 'bold', color: 'white', fontSize: '16px', margin: '0 0 4px 0', display: 'flex', alignItems: 'center' }}>
                          <span style={{ marginRight: '6px' }}>
                            {payout.type === 'paypal' ? '💳' : payout.type === 'tremendous_gift_card' ? '🎁' : '🎫'}
                          </span>
                          {formatCurrency(payout.amount)} {payout.type === 'paypal' ? 'PayPal Payout' : 'Gift Card'}
                        </p>
                        
                        {/* Show different info based on payout type */}
                        {payout.type === 'paypal' ? (
                          <>
                            <p style={{ color: '#C4B5FD', fontSize: '14px', margin: '0 0 4px 0' }}>
                              PayPal: {payout.recipientEmail}
                            </p>
                            <p style={{ color: '#C4B5FD', fontSize: '12px', fontFamily: 'monospace', margin: '0 0 4px 0' }}>
                              Batch ID: {payout.paypalBatchId || 'N/A'}
                            </p>
                            <p style={{ color: '#9CA3AF', fontSize: '12px', margin: '0 0 8px 0' }}>
                              {payout.timestamp?.toLocaleDateString()}
                            </p>
                            {payout.status === 'SUCCESS' && (
                              <p style={{ color: '#10B981', fontSize: '11px', margin: '0' }}>
                                ✅ Successfully sent to PayPal
                              </p>
                            )}
                            {payout.status === 'PENDING' && (
                              <p style={{ color: '#F59E0B', fontSize: '11px', margin: '0' }}>
                                ⏳ Processing by PayPal
                              </p>
                            )}
                            {payout.status === 'UNCLAIMED' && (
                              <p style={{ color: '#EA580C', fontSize: '11px', margin: '0' }}>
                                ⏰ Waiting for recipient to claim
                              </p>
                            )}
                            {(payout.status === 'FAILED' || payout.status === 'BLOCKED' || payout.status === 'DENIED') && (
                              <p style={{ color: '#EF4444', fontSize: '11px', margin: '0' }}>
                                ❌ Payout failed - balance restored
                              </p>
                            )}
                            {payout.status === 'RETURNED' && (
                              <p style={{ color: '#EF4444', fontSize: '11px', margin: '0' }}>
                                ↩️ Returned unclaimed - balance restored
                              </p>
                            )}
                          </>
                        ) : (
                          <>
                            {payout.type && (
                              <p style={{ color: '#C4B5FD', fontSize: '14px', margin: '0 0 4px 0' }}>
                                {payout.type === 'tremendous_gift_card' ? 'Gift Card' : 'Voucher Code'}
                              </p>
                            )}
                            
                            <p style={{ color: '#C4B5FD', fontSize: '12px', fontFamily: 'monospace', margin: '0 0 4px 0' }}>
                              {payout.voucherCode}
                            </p>
                            
                            <p style={{ color: '#9CA3AF', fontSize: '12px', margin: '0 0 8px 0' }}>
                              {payout.timestamp?.toLocaleDateString()}
                            </p>

                            {payout.tremendousOrderId && (
                              <p style={{ color: '#60A5FA', fontSize: '11px', margin: '0 0 4px 0' }}>
                                Order: {payout.tremendousOrderId}
                              </p>
                            )}

                            {payout.delivered && (
                              <p style={{ color: '#10B981', fontSize: '11px', margin: '0' }}>
                                ✅ Delivered via email
                              </p>
                            )}
                          </>
                        )}
                      </div>
                      
                      <div style={{ textAlign: 'right', marginLeft: '16px' }}>
                        {payout.type === 'paypal' ? (
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '8px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            backgroundColor: 
                              payout.status === 'SUCCESS' ? '#065F46' : 
                              payout.status === 'PENDING' ? '#92400E' : 
                              payout.status === 'UNCLAIMED' ? '#7C2D12' :
                              payout.status === 'RETURNED' || payout.status === 'FAILED' || payout.status === 'BLOCKED' || payout.status === 'DENIED' ? '#7F1D1D' :
                              '#374151',
                            color: 
                              payout.status === 'SUCCESS' ? '#A7F3D0' : 
                              payout.status === 'PENDING' ? '#FCD34D' : 
                              payout.status === 'UNCLAIMED' ? '#FED7AA' :
                              payout.status === 'RETURNED' || payout.status === 'FAILED' || payout.status === 'BLOCKED' || payout.status === 'DENIED' ? '#FCA5A5' :
                              '#9CA3AF',
                            border: `2px solid ${
                              payout.status === 'SUCCESS' ? '#10B981' : 
                              payout.status === 'PENDING' ? '#F59E0B' : 
                              payout.status === 'UNCLAIMED' ? '#EA580C' :
                              payout.status === 'RETURNED' || payout.status === 'FAILED' || payout.status === 'BLOCKED' || payout.status === 'DENIED' ? '#EF4444' :
                              '#6B7280'
                            }`,
                            display: 'inline-block'
                          }}>
                            {payout.status?.toUpperCase() || 'COMPLETED'}
                          </span>
                        ) : (
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '8px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            backgroundColor: statusColors.bg,
                            color: statusColors.text,
                            border: `2px solid ${statusColors.border}`,
                            display: 'inline-block'
                          }}>
                            {payout.status?.toUpperCase()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '32px', color: '#9CA3AF' }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>💳</div>
              <p>
                {payoutFilter === 'all' ? 'No payouts made yet' : 
                 payoutFilter === 'paypal' ? 'No PayPal payouts found' :
                 payoutFilter === 'voucher' ? 'No gift cards found' :
                 `No ${payoutFilter} payouts found`}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Info Section */}
      <div style={{
        marginTop: '32px',
        backgroundColor: '#1E3A8A',
        border: '3px solid #3B82F6',
        borderRadius: '16px',
        padding: '24px'
      }}>
        <h3 style={{ fontWeight: 'bold', color: '#BFDBFE', fontSize: '18px', marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: '12px' }}>💡</span>
          How It Works
        </h3>
        <div style={{ color: '#BFDBFE', fontSize: '14px', lineHeight: '1.6' }}>
          <p style={{ margin: '0 0 8px 0' }}>• Earn money when fans request songs and rate your performance</p>
          <p style={{ margin: '0 0 8px 0' }}>• RequestASong keeps a platform fee to maintain the platform</p>
          <p style={{ margin: '0 0 8px 0' }}>• Redeem your earnings as gift cards or via PayPal (minimum $20 for both to minimize fees)</p>
          <p style={{ margin: '0 0 8px 0' }}>• Gift cards are delivered directly to your email</p>
          <p style={{ margin: 0 }}>• Higher ratings = better reputation = more requests!</p>
        </div>
      </div>
    </div>
  );
}